import { useEffect, useMemo, useState, useCallback } from 'react';
import Card from '../../../components/base/Card';
import Button from '../../../components/base/Button';

interface DashboardProps {
  user: {
    name: string;
    role: string;
  };
}

// Matches your API response
interface FileEntry {
  id: string;
  displayName: string;
  fileDescription?: string | null;
  shortDescription?: string | null;
  fileType: string;          // e.g., 'pdf', 'docx', 'pptx', 'png', 'mov'
  fileSize: number;          // bytes
  folderPath: string;        // e.g., 'root/marketing'
  visibility: string[];      // e.g., ['Investor', 'Public']
  tags?: string[] | null;
  domain: string;
  createdAt?: string;
  updatedAt?: string;
}

interface MeProfile {
  id: string;
  email: string;
  name?: string | null;
  domain: string;
  attributes: string[];
  isAdmin: boolean;
  createdAt?: string;
}

interface DocumentCategory {
  id: string;        // folder root id
  name: string;      // display name
  icon: string;      // icon class
  count: number;     // number of files in this category
  lastUpdated: string; // human friendly updated string
}

const API_BASE = (import.meta as any).env?.VITE_API_BASE_URL || 'http://localhost:9080';

// Helpers
const PREVIEWABLE = new Set(['pdf','png','jpg','jpeg','gif','webp','txt','mov','mpg','mpeg','mp4']);

const mimeFor = (entry: FileEntry, fallback?: string) => {
  const t = (entry.fileType || '').toLowerCase();
  switch (t) {
    case 'pdf': return 'application/pdf';
    case 'png': return 'image/png';
    case 'jpg':
    case 'jpeg': return 'image/jpeg';
    case 'gif': return 'image/gif';
    case 'webp': return 'image/webp';
    case 'txt': return 'text/plain';
    case 'mov': return 'video/quicktime';
    case 'mpg':
    case 'mpeg': return 'video/mpeg';
    case 'mp4': return 'video/mp4';
    default: return fallback || 'application/octet-stream';
  }
};

const sanitizeFilename = (name: string) => name.replace(/[\\/:*?"<>|]+/g, '_').trim();
const withExtension = (name: string, ext?: string) => {
  if (!ext || !ext.trim()) return name;
  const e = ext.trim();
  return name.toLowerCase().endsWith(`.${e.toLowerCase()}`) ? name : `${name}.${e}`;
};

const formatBytes = (bytes: number) => {
  if (!Number.isFinite(bytes)) return '';
  const units = ['B','KB','MB','GB','TB'];
  let i = 0; let val = bytes;
  while (val >= 1024 && i < units.length - 1) { val /= 1024; i++; }
  return `${val.toFixed(val < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
};

const iconForType = (t: string) => {
  const type = t?.toLowerCase();
  if (type === 'pdf') return 'ri-file-pdf-line text-accent-600';
  if (type === 'doc' || type === 'docx') return 'ri-file-word-line text-accent-600';
  if (type === 'ppt' || type === 'pptx') return 'ri-file-ppt-line text-accent-600';
  if (type === 'xls' || type === 'xlsx' || type === 'csv') return 'ri-file-excel-line text-accent-600';
  if (['png','jpg','jpeg','gif','webp','svg','bmp','avif'].includes(type)) return 'ri-image-line text-accent-600';
  if (['mp4','m4v','webm','mov','mpg','mpeg','avi','mkv'].includes(type)) return 'ri-film-line text-accent-600';
  return 'ri-file-line text-slate-500';
};

const iconForCategory = (raw: string) => {
  const n = (raw || '').toLowerCase();
  // Core mappings
  if (n.includes('sales')) return 'ri-shopping-bag-3-line';
  if (n.includes('account')) return 'ri-calculator-line'; // accounting, accounts
  if (n.includes('market')) return 'ri-megaphone-line';
  if (n.includes('document') || n === 'docs' || n === 'documents') return 'ri-file-2-line';
  if (n.includes('report')) return 'ri-file-chart-line';
  if (n.includes('finance') || n.includes('financial')) return 'ri-bar-chart-box-line';
  if (n.includes('investor') || n.includes('investment') || n === 'ir') return 'ri-hand-coin-line';

  // Additional useful mappings
  if (n.includes('legal')) return 'ri-scales-3-line';
  if (n === 'hr' || n.includes('human')) return 'ri-team-line';
  if (n.includes('operation') || n === 'ops') return 'ri-settings-3-line';
  if (n.includes('engineer') || n.includes('tech') || n.includes('technology')) return 'ri-cpu-line';
  if (n.includes('product')) return 'ri-shapes-line';
  if (n.includes('security')) return 'ri-lock-2-line';
  if (n.includes('compliance')) return 'ri-shield-check-line';
  if (n.includes('contract')) return 'ri-file-list-3-line';
  if (n.includes('partner') || n.includes('vendor')) return 'ri-links-line';
  if (n.includes('media')) return 'ri-image-2-line';
  if (n.includes('press') || n.includes('pr')) return 'ri-newspaper-line';
  if (n.includes('brand') || n.includes('design') || n.includes('creative') || n.includes('asset')) return 'ri-palette-line';
  if (n.includes('board') || n.includes('govern')) return 'ri-building-2-line';
  if (n.includes('tax')) return 'ri-bill-line';
  if (n.includes('audit')) return 'ri-search-eye-line';
  if (n.includes('roadmap') || n.includes('strategy')) return 'ri-rocket-line';
  if (n.includes('analytic') || n.includes('metric') || n.includes('kpi')) return 'ri-pie-chart-2-line';
  if (n.includes('data')) return 'ri-database-2-line';
  if (n.includes('research') || n.includes('lab')) return 'ri-flask-line';
  if (n.includes('present') || n.includes('pitch') || n.includes('slides')) return 'ri-slideshow-2-line';

  // Fallback
  return 'ri-folder-line';
};

export default function Dashboard({ user }: DashboardProps) {
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [files, setFiles] = useState<FileEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [me, setMe] = useState<MeProfile | null>(null);

  // Track expanded rows to reveal full description
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());
  const toggleExpanded = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Track which files are downloading (to show a spinner per row)
  const [downloadingIds, setDownloadingIds] = useState<Set<string>>(() => new Set());
  const startDownloading = (id: string) => setDownloadingIds(prev => {
    const next = new Set(prev);
    next.add(id);
    return next;
  });
  const stopDownloading = (id: string) => setDownloadingIds(prev => {
    const next = new Set(prev);
    next.delete(id);
    return next;
  });

  // Helper: prefer fileDescription; fall back to any `description` if present
  const fullDescriptionOf = (f: FileEntry): string | null => {
    return (f.fileDescription ?? (f as any).description ?? null) as string | null;
  };

  // Attribute precedence and subtitle copy
  const ATTR_ORDER = ["Internal", "Investor", "Marketing", "Partner", "Public"] as const;
  const pickPrimaryAttr = (attrs?: string[]) => {
    if (!attrs || attrs.length === 0) return undefined;
    for (const key of ATTR_ORDER) {
      if (attrs.includes(key)) return key as string;
    }
    return undefined;
  };

  const primaryAttr = useMemo(() => pickPrimaryAttr(me?.attributes), [me]);

  const subtitle = useMemo(() => {
    switch (primaryAttr) {
      case 'Internal':
        return 'Access internal documents, operations materials, and confidential resources';
      case 'Investor':
        return 'Access your documents and explore investment opportunities';
      case 'Marketing':
        return 'Access brand assets, campaigns, and marketing collateral';
      case 'Partner':
        return 'Access partner resources, agreements, and shared materials';
      case 'Public':
        return 'Browse public documents and resources';
      default:
        return 'Access your documents';
    }
  }, [primaryAttr]);

  const fetchFiles = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/files`, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
        },
      });
      if (res.status === 401) {
        localStorage.removeItem('token');
        window.location.href = '/login';
        return;
      }
      if (!res.ok) {
        let reason = `Failed to load files (${res.status})`;
        try { const data = await res.json(); if (data?.reason) reason = data.reason; } catch {}
        throw new Error(reason);
      }
      const data = await res.json();
      setFiles(data as FileEntry[]);
    } catch (e: any) {
      setError(e?.message || 'Network error');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch files on mount
  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  useEffect(() => {
    const loadMe = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_BASE}/me`, {
          headers: { 'Authorization': token ? `Bearer ${token}` : '' },
        });
        if (res.status === 401) {
          localStorage.removeItem('token');
          window.location.href = '/login';
          return;
        }
        if (res.ok) {
          const data = await res.json();
          setMe(data as MeProfile);
        }
      } catch {}
    };
    loadMe();
  }, []);

  // Build categories from second segment of folderPath (e.g., 'root/marketing' -> 'marketing')
  const categories: DocumentCategory[] = useMemo(() => {
    const map = new Map<string, { count: number; lastCreatedAt?: number }>();
    for (const f of files) {
      const parts = (f.folderPath || '').split('/').filter(Boolean);
      // Use only the secondary segment after the top-level (e.g., 'root/marketing' -> 'marketing')
      const second = parts.length > 1 ? parts[1] : undefined;
      if (!second) continue; // skip top-level root-only paths

      const createdMs = f.createdAt ? Date.parse(f.createdAt) : NaN;
      const current = map.get(second) || { count: 0, lastCreatedAt: undefined };
      current.count += 1;
      if (!Number.isNaN(createdMs)) {
        current.lastCreatedAt = Math.max(current.lastCreatedAt ?? 0, createdMs);
      }
      map.set(second, current);
    }
    return Array.from(map.entries()).map(([id, meta]) => ({
      id,
      name: id.charAt(0).toUpperCase() + id.slice(1),
      icon: iconForCategory(id),
      count: meta.count,
      lastUpdated: meta.lastCreatedAt ? new Date(meta.lastCreatedAt).toLocaleString() : '',
    }));
  }, [files]);

  const displayName = useMemo(() => {
    if (me?.name && me.name.trim()) return me.name;
    if (me?.email) return me.email;
    if (user?.name) return user.name;
    return 'Guest';
  }, [me, user]);

  const filteredDocuments = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return files.filter((f) => {
      const parts = (f.folderPath || '').split('/').filter(Boolean);
      const second = parts.length > 1 ? parts[1] : undefined;
      const inCategory = !selectedCategory || second === selectedCategory;
      const hay = [
        f.displayName,
        f.shortDescription || '',
        f.fileDescription || '',
        (f.tags || []).join(' '),
      ].join(' ').toLowerCase();
      const matches = !term || hay.includes(term);
      return inCategory && matches;
    });
  }, [files, searchTerm, selectedCategory]);

  const onDownload = async (entry: FileEntry) => {
    startDownloading(entry.id);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/files/${entry.id}/download`, {
        headers: { 'Authorization': token ? `Bearer ${token}` : '' },
      });
      if (!res.ok) throw new Error(`Download failed (${res.status})`);

      const blob = await res.blob();
      // Prefer Content-Disposition filename if provided
      const cd = res.headers.get('content-disposition') || undefined;
      let filename = withExtension(sanitizeFilename(entry.displayName || 'download'), entry.fileType);
      if (cd) {
        const match = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(cd);
        const fromHeader = match ? decodeURIComponent(match[1] || match[2] || '') : '';
        if (fromHeader) filename = fromHeader;
      }

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      a.remove();

      // Safari/Chrome can fail if we revoke immediately; delay cleanup.
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (e: any) {
      setError(e?.message || 'Download failed');
    } finally {
      stopDownloading(entry.id);
    }
  };

  const onPreview = async (entry: FileEntry) => {
    const w = window.open('', '_blank');
    if (!w) { setError('Pop-up blocked. Allow pop-ups to preview.'); return; }
    w.document.title = `Preview: ${entry.displayName}`;
    w.document.body.innerHTML = '<p style="font-family: system-ui, sans-serif; padding: 16px;">Loading preview…</p>';

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/files/${entry.id}/download`, {
        headers: { 'Authorization': token ? `Bearer ${token}` : '' },
      });
      if (!res.ok) throw new Error(`Preview failed (${res.status})`);

      const headerType = res.headers.get('content-type') || undefined;
      const data = await res.blob();
      const type = mimeFor(entry, headerType || undefined);
      const blob = new Blob([data], { type });
      const url = URL.createObjectURL(blob);

      // Use href (not replace) and delay revocation so the viewer has time to load.
      w.location.href = url;
      w.addEventListener('beforeunload', () => {
        try { URL.revokeObjectURL(url); } catch {}
      });
      setTimeout(() => {
        try { URL.revokeObjectURL(url); } catch {}
      }, 5 * 60_000);
    } catch (e: any) {
      try {
        w.document.body.innerHTML = '<p style="font-family: system-ui, sans-serif; padding: 16px; color: #b91c1c;">Preview failed. Please try Download instead.</p>';
      } catch {
        w.close();
      }
      setError(e?.message || 'Preview failed');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Dashboard Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Welcome back, {displayName}</h1>
              <p className="text-slate-600 mt-1">{subtitle}</p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="bg-gradient-to-r from-accent-50 to-accentAlt-50 px-4 py-2 rounded-lg border border-accent-200">
                <div className="flex items-center space-x-2">
                  <i className="ri-shield-check-line text-accent-600"></i>
                  <span className="text-sm font-medium text-accent-700">Secure Access Granted</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Search and Filters */}
        <div className="mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center space-y-4 lg:space-y-0 lg:space-x-4">
            <div className="flex-1 relative">
              <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400"></i>
              <input
                type="text"
                placeholder="Search documents..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-transparent"
              />
            </div>
            <Button
              variant={selectedCategory ? 'secondary' : 'outline'}
              onClick={() => setSelectedCategory('')}
            >
              <i className="ri-folder-line mr-2"></i>
              All Categories
            </Button>
            <Button
              variant="outline"
              onClick={fetchFiles}
              disabled={loading}
            >
              {loading ? (
                <>
                  <i className="ri-loader-4-line animate-spin mr-2"></i>
                  Refreshing…
                </>
              ) : (
                <>
                  <i className="ri-refresh-line mr-2"></i>
                  Refresh
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Document Categories Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {categories.map((category) => (
            <Card
              key={category.id}
              hover
              onClick={() => setSelectedCategory(category.id)}
              className={`p-6 ${selectedCategory === category.id ? 'ring-2 ring-accent-500 border-accent-300' : ''}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center bg-gradient-to-r from-accent-100 to-accentAlt-100`}>
                  <i className={`${category.icon} text-xl text-accent-600`}></i>
                </div>
              </div>
              
              <h3 className="text-lg font-semibold text-slate-900 mb-2">{category.name}</h3>
              <p className="text-sm text-slate-600 mb-4">{category.count} document{category.count === 1 ? '' : 's'}</p>
              <div className="flex items-center justify-end text-sm">
                <i className="ri-time-line text-slate-400 mr-2"></i>
                <span className="text-slate-500">Updated</span>
                <span className="mx-1 text-slate-400">•</span>
                <span className="text-slate-400">{category.lastUpdated || '—'}</span>
              </div>
            </Card>
          ))}
        </div>

        {/* Recent Documents */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200">
          <div className="px-6 py-4 border-b border-slate-200">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-slate-900">
                {selectedCategory ? categories.find(c => c.id === selectedCategory)?.name : 'All Documents'}
              </h2>
              <span className="text-sm text-slate-500">{filteredDocuments.length} documents</span>
            </div>
          </div>
          
          {loading && (
            <div className="px-6 py-6 text-slate-500">Loading documents…</div>
          )}
          {error && (
            <div className="px-6 py-6 text-red-600">{error}</div>
          )}

          {!loading && !error && (
            <div className="divide-y divide-slate-200">
              {filteredDocuments.map((doc) => (
                <div key={doc.id} className="px-6 py-4 hover:bg-slate-50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-r from-accent-50 to-accentAlt-50 border border-accent-100">
                        <i className={`${iconForType(doc.fileType)}`}></i>
                      </div>
                      <div>
                        <div className="flex items-center space-x-2">
                          <h3 className="font-medium text-slate-900">{withExtension(doc.displayName, doc.fileType)}</h3>
                        </div>
                        <div className="text-sm">
                          <div className="flex items-center flex-wrap gap-x-4 text-slate-500">
                            <span>{formatBytes(doc.fileSize)}</span>
                            {doc.shortDescription && <span>{doc.shortDescription}</span>}
                            {fullDescriptionOf(doc) && !expandedIds.has(doc.id) && (
                              <button
                                type="button"
                                onClick={() => toggleExpanded(doc.id)}
                                className="text-accent-700 hover:underline"
                              >
                                ...more
                              </button>
                            )}
                            {doc.tags && doc.tags.length > 0 && <span>Tags: {doc.tags.join(', ')}</span>}
                          </div>
                          {expandedIds.has(doc.id) && fullDescriptionOf(doc) && (
                            <div className="mt-2 text-slate-700">
                              {fullDescriptionOf(doc)}
                              <button
                                type="button"
                                onClick={() => toggleExpanded(doc.id)}
                                className="ml-2 text-accent-700 hover:underline"
                              >
                                show less
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onPreview(doc)}
                        disabled={!PREVIEWABLE.has((doc.fileType || '').toLowerCase())}
                      >
                        <i className="ri-eye-line mr-2"></i>
                        Preview
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => onDownload(doc)}
                        disabled={downloadingIds.has(doc.id)}
                      >
                        {downloadingIds.has(doc.id) ? (
                          <>
                            <i className="ri-loader-4-line animate-spin mr-2"></i>
                            Downloading…
                          </>
                        ) : (
                          <>
                            <i className="ri-download-line mr-2"></i>
                            Download
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
